/**
 * A&D standard format (FX-i/FZ-i, FX/FZ, GX, GF, EK...; research/09 §2.3, §2.5): `ST,+00012.345  g` + CR LF.
 * Headers: ST stable, US unstable, OL overload (`OL,+9999999E+19`), QT counting. With "AK and error code" on,
 * commands are answered with ACK (06h) or `EC,Exx`. George's FX-123 has no USB module: it speaks this over
 * RS-232 through a USB–serial adapter, which must be wired straight through because the balance is DCE.
 */
import { ACK } from "../lines";
import type { BalanceEvent, BalanceProtocol } from "../types";
import { toGrams } from "./units";

const READING = /^(ST|US|OL|QT),\s*([+-]?)\s*([\d.]*)(?:E[+-]?\d+)?\s*([A-Za-z%]*)\s*$/;
const IDENTITY = /^(TN|SN|ID)\s*,\s*"?\s*(.*?)\s*"?\s*$/;
const ERROR = /^EC\s*,\s*(E\d{2})/;

const ERRORS: Record<string, string> = {
  E00: "The balance reported a communication error",
  E01: "The balance did not understand the command",
  E02: "The balance is not ready for that command",
  E03: "The balance timed out waiting for the command",
  E04: "The command was too long",
  E06: "The balance rejected the data format",
  E07: "The balance rejected the setting",
  E11: "The balance could not stabilise (check for drafts or vibration)",
  E20: "Calibration weight error (too heavy)",
  E21: "Calibration weight error (too light)",
};

const IDENTITY_FIELDS = { TN: "model", SN: "serial", ID: "id" } as const;

export function parseAndLine(line: string, at: number): BalanceEvent | null {
  if (line === ACK) return { type: "ack", raw: line };
  const trimmed = line.trim();
  const reading = READING.exec(trimmed);
  if (reading) {
    const [, header = "", sign = "", digits = "", unit = ""] = reading;
    const overload = header === "OL";
    return {
      type: "reading",
      reading: {
        grams: overload || header === "QT" ? null : toGrams(sign, digits, unit),
        unit,
        stable: header === "ST" ? true : header === "US" ? false : null,
        overload,
        raw: line,
        at,
      },
    };
  }
  const identity = IDENTITY.exec(trimmed);
  if (identity) {
    const [, key = "TN", value = ""] = identity;
    return { type: "identity", field: IDENTITY_FIELDS[key as keyof typeof IDENTITY_FIELDS], value, raw: line };
  }
  const error = ERROR.exec(trimmed);
  if (error) {
    const code = error[1] ?? "E00";
    return { type: "error", code, message: ERRORS[code] ?? `The balance reported error ${code}`, raw: line };
  }
  return null;
}

export const andProtocol: BalanceProtocol = {
  id: "and",
  label: "A&D (FX-i, GX, GF, EK)",
  parse: parseAndLine,
  commands: {
    identify: ["?TN", "?SN"],
    startStream: "SIR",
    stopStream: "C",
    stable: "S",
    immediate: "Q",
    tare: "T",
    zero: "R",
  },
  lineEnding: "\r\n",
  // The RS-232 factory setting; the FX-05 USB virtual COM port ignores serial parameters.
  serial: { baudRate: 2400, dataBits: 7, parity: "even", stopBits: 1 },
};
